// ═══════════════════════════════════════════════════════════════════════════════
// CENTRALIZED SHARE PERMISSIONS UTILITY
// Frontend version - mirrors backend sharePermissions.js
// All share-related constants for projects and chats
// ═══════════════════════════════════════════════════════════════════════════════

import { USER_TYPES } from './permissions';

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1: PROJECT SHARE PERMISSIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Project share permission presets
 * - view: Can only view project and files
 * - full: Can do everything except delete project
 * - custom: User-defined permissions
 */
export const PROJECT_SHARE_PRESETS = {
  view: {
    key: 'view',
    label: 'View Only',
    description: 'Can view project, files, download files, and create chat sessions',
    permissions: {
      can_view: true,
      can_view_files: true,
      can_download_files: true,
      can_upload_files: false,
      can_delete_files: false,
      can_reindex_files: false,
      can_create_chats: true,
      can_share: false,
      inference_only: false,
    },
  },
  full: {
    key: 'full',
    label: 'Full Access',
    description: 'Can do everything except delete project',
    permissions: {
      can_view: true,
      can_view_files: true,
      can_download_files: true,
      can_upload_files: true,
      can_delete_files: true,
      can_reindex_files: true,
      can_create_chats: true,
      can_share: true,
      inference_only: false,
    },
  },
  inference_only: {
    key: 'inference_only',
    label: 'Inference Only',
    description: 'Can only create and use chat sessions, project hidden from library',
    permissions: {
      can_view: false,
      can_view_files: false,
      can_download_files: false,
      can_upload_files: false,
      can_delete_files: false,
      can_reindex_files: false,
      can_create_chats: true,
      can_share: false,
      inference_only: true,
    },
  },
  custom: {
    key: 'custom',
    label: 'Custom',
    description: 'Choose specific permissions',
    permissions: null,
  },
};

/**
 * Project custom permission options (for UI)
 */
export const PROJECT_CUSTOM_OPTIONS = [
  { key: 'can_view', label: 'View Project', alwaysTrue: true },
  { key: 'can_view_files', label: 'View Files' },
  { key: 'can_download_files', label: 'Download Files' },
  { key: 'can_upload_files', label: 'Upload Files' },
  { key: 'can_delete_files', label: 'Delete Files' },
  { key: 'can_reindex_files', label: 'Reindex Files' },
  { key: 'can_create_chats', label: 'Create Insight Engine Sessions' },
  { key: 'can_share', label: 'Share Project' },
];

/**
 * Default project permissions (for view only)
 */
export const DEFAULT_PROJECT_PERMISSIONS = {
  can_view: true,
  can_view_files: true,
  can_download_files: true,
  can_upload_files: false,
  can_delete_files: false,
  can_reindex_files: false,
  can_create_chats: false,
  can_share: false,
  inference_only: false,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2: CHAT SHARE PERMISSIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Chat share permission presets
 * - view: Can only view chat history
 * - full: Can view, send messages, and share
 * - custom: User-defined permissions
 */
export const CHAT_SHARE_PRESETS = {
  view: {
    key: 'view',
    label: 'View Only',
    description: 'Can view chat history only',
    permissions: {
      can_view: true,
      can_send_messages: false,
      can_share: false,
    },
  },
  full: {
    key: 'full',
    label: 'Full Access',
    description: 'Can view, send messages, and share',
    permissions: {
      can_view: true,
      can_send_messages: true,
      can_share: true,
    },
  },
  custom: {
    key: 'custom',
    label: 'Custom',
    description: 'Choose specific permissions',
    permissions: null,
  },
};

/**
 * Chat custom permission options (for UI)
 */
export const CHAT_CUSTOM_OPTIONS = [
  { key: 'can_view', label: 'View Chat', alwaysTrue: true },
  { key: 'can_send_messages', label: 'Send Messages' },
  { key: 'can_share', label: 'Share Chat' },
];

/**
 * Default chat permissions (for view only)
 */
export const DEFAULT_CHAT_PERMISSIONS = {
  can_view: true,
  can_send_messages: false,
  can_share: false,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3: PERMISSION RESOLVERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolve project permissions from preset key or custom permissions
 * @param {string} presetKey - 'view', 'full', or 'custom'
 * @param {Object} customPermissions - Custom permissions when presetKey is 'custom'
 * @returns {Object} Resolved permissions object
 */
export const resolveProjectPermissions = (presetKey, customPermissions = {}) => {
  // If preset key matches a preset, return those permissions
  if (PROJECT_SHARE_PRESETS[presetKey] && PROJECT_SHARE_PRESETS[presetKey].permissions) {
    return { ...PROJECT_SHARE_PRESETS[presetKey].permissions };
  }

  // For 'custom' or unknown, use provided permissions with defaults
  // Note: can_download_files defaults to false for NEW custom shares
  // Backward compatibility for OLD shares (without this field) is handled in hasSharePermission
  return {
    can_view: customPermissions.can_view ?? true,
    can_view_files: customPermissions.can_view_files ?? false,
    can_download_files: customPermissions.can_download_files ?? false,
    can_upload_files: customPermissions.can_upload_files ?? false,
    can_delete_files: customPermissions.can_delete_files ?? false,
    can_reindex_files: customPermissions.can_reindex_files ?? false,
    can_create_chats: customPermissions.can_create_chats ?? false,
    can_share: customPermissions.can_share ?? false,
    inference_only: customPermissions.inference_only ?? false,
  };
};

/**
 * Resolve chat permissions from preset key or custom permissions
 * @param {string} presetKey - 'view', 'full', or 'custom'
 * @param {Object} customPermissions - Custom permissions when presetKey is 'custom'
 * @returns {Object} Resolved permissions object
 */
export const resolveChatPermissions = (presetKey, customPermissions = {}) => {
  // If preset key matches a preset, return those permissions
  if (CHAT_SHARE_PRESETS[presetKey] && CHAT_SHARE_PRESETS[presetKey].permissions) {
    return { ...CHAT_SHARE_PRESETS[presetKey].permissions };
  }

  // For 'custom' or unknown, use provided permissions with defaults
  return {
    can_view: true, // Always true
    can_send_messages: customPermissions.can_send_messages ?? false,
    can_share: customPermissions.can_share ?? false,
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 4: PERMISSION CHECKERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user has a specific permission from share info
 * @param {Object} sharePermissions - The permissions object from API response
 * @param {string} permissionKey - Permission key (e.g., 'can_upload_files')
 * @returns {boolean}
 */
export const hasSharePermission = (sharePermissions, permissionKey) => {
  if (!sharePermissions) return false;
  return sharePermissions[permissionKey] === true;
};

/**
 * Check if project is shared to current user (from API response)
 * @param {Object} project - Project object from API
 * @returns {boolean}
 */
export const isProjectSharedToMe = (project) => {
  return project?.sharedToMe === true;
};

/**
 * Check if chat is shared to current user (from API response)
 * @param {Object} chat - Chat object from API
 * @returns {boolean}
 */
export const isChatSharedToMe = (chat) => {
  return chat?.sharedToMe === true;
};

/**
 * Get share permissions from project (from API response)
 * @param {Object} project - Project object from API
 * @returns {Object|null}
 */
export const getProjectSharePermissions = (project) => {
  if (!isProjectSharedToMe(project)) return null;
  return project.sharePermissions || null;
};

/**
 * Get share permissions from chat (from API response)
 * @param {Object} chat - Chat object from API
 * @returns {Object|null}
 */
export const getChatSharePermissions = (chat) => {
  if (!isChatSharedToMe(chat)) return null;
  return chat.sharePermissions || null;
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 5: COMBINED PERMISSION HELPER
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Check if user can perform action on project
 * Combines owner check + admin check + share permission check
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.project - Project object from API
 * @param {string} params.userId - Current user ID
 * @param {string} params.userType - Current user type (from permissions.js)
 * @param {string} params.permissionKey - Permission to check (e.g., 'can_upload_files')
 * @returns {boolean}
 */
export const canUserPerformProjectAction = ({ project, userId, userType, permissionKey }) => {
  if (!project) return false;

  // Only Super Admin can perform any action - others need owner/shared
  if (userType === USER_TYPES.SUPER_ADMIN) {
    return true;
  }

  // Owner has full access
  const isOwner = project.created_by?._id === userId || project.created_by === userId;
  if (isOwner) {
    return true;
  }

  // Check share permissions (includes admin if shared to them)
  if (isProjectSharedToMe(project)) {
    return hasSharePermission(project.sharePermissions, permissionKey);
  }

  return false;
};

/**
 * Check if user can perform action on chat
 * Combines owner check + admin check + share permission check
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.chat - Chat object from API
 * @param {string} params.userId - Current user ID
 * @param {string} params.userType - Current user type (from permissions.js)
 * @param {string} params.permissionKey - Permission to check (e.g., 'can_send_messages')
 * @returns {boolean}
 */
export const canUserPerformChatAction = ({ chat, userId, userType, permissionKey }) => {
  if (!chat) return false;

  // Only Super Admin can perform any action - others need owner/shared
  if (userType === USER_TYPES.SUPER_ADMIN) {
    return true;
  }

  // Owner has full access
  const isOwner = chat.created_by?._id === userId || chat.created_by === userId;
  if (isOwner) {
    return true;
  }

  // Check share permissions (includes admin if shared to them)
  if (isChatSharedToMe(chat)) {
    return hasSharePermission(chat.sharePermissions, permissionKey);
  }

  return false;
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 6: DISPLAY HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get display label for permission preset
 * @param {Object} permissions - Permissions object
 * @param {string} entityType - 'project' or 'chat'
 * @returns {string}
 */
export const getPermissionLabel = (permissions, entityType = 'project') => {
  const presets = entityType === 'project' ? PROJECT_SHARE_PRESETS : CHAT_SHARE_PRESETS;

  // Check if permissions match any preset
  for (const [key, preset] of Object.entries(presets)) {
    if (key === 'custom' || !preset.permissions) continue;

    const isMatch = Object.keys(preset.permissions).every(
      (permKey) => permissions?.[permKey] === preset.permissions[permKey]
    );

    if (isMatch) {
      return preset.label;
    }
  }

  return 'Custom';
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 7: EXPORTS (Default object for convenience)
// ═══════════════════════════════════════════════════════════════════════════════

const sharePermissions = {
  // Project constants
  PROJECT_SHARE_PRESETS,
  PROJECT_CUSTOM_OPTIONS,
  DEFAULT_PROJECT_PERMISSIONS,

  // Chat constants
  CHAT_SHARE_PRESETS,
  CHAT_CUSTOM_OPTIONS,
  DEFAULT_CHAT_PERMISSIONS,

  // Resolvers
  resolveProjectPermissions,
  resolveChatPermissions,

  // Permission checkers
  hasSharePermission,
  isProjectSharedToMe,
  isChatSharedToMe,
  getProjectSharePermissions,
  getChatSharePermissions,

  // Combined helpers
  canUserPerformProjectAction,
  canUserPerformChatAction,

  // Display helpers
  getPermissionLabel,
};

export default sharePermissions;
